//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useState } from 'react';
import { Box, IconButton, IconButtonProps, Tooltip, Typography } from '@mui/material';
import copy from 'copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useSnackbar } from '@/hooks/useSnackbar';

// A copy button.
//
// This component provides a consistent UI pattern for a copy button.
//
// @param {string} textToCopy - The text to put in the buffer.
// @param {string} displayName - Name to append to "Copy ..." titles.
// @param {string} size - Size of the button.
// @param {string} fontSize - Size of the icon inside the button.
// @param {boolean} showToast - True to show a snackbar popup.
// @param {boolean} captionBelow - True to show "Copied" below the icon on copy.
interface CopyButtonProps extends IconButtonProps {
  textToCopy: string | (() => string);
  /**
   * Whether there is nothing to copy.  Worked out from the text where it is text; where it is
   * a function -- handed by a caller that would rather not render this again per letter -- the
   * caller is the one that knows.
   */
  disabled?: boolean;
  displayName?: string;
  fontSize?: string;
  showToast?: boolean;
  captionBelow?: boolean;
}

export default function CopyButton({ textToCopy, displayName = undefined,
                                     size = 'medium', fontSize = 'medium',
                                     showToast = false, captionBelow = true,
                                     disabled, ...props }: CopyButtonProps) {
  const showSnackbar = useSnackbar();
  const [copied, setCopied] = useState(false);

  const title =
      `${copied ? 'Copied' : 'Copy'}${displayName ? ` ${displayName}` : ''}`;

  const handleCopy = () => {
    copy(typeof textToCopy === 'function' ? textToCopy() : textToCopy);
    if (showToast) {
      const snackbarText = displayName ? `${displayName} copied to clipboard.` :
                           'Copied to clipboard.';
      showSnackbar(snackbarText, 'success');
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip describeChild title={title}>
      <span>
        <IconButton
          onClick={handleCopy}
          // Prevent focus loss when clicking button.
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled ??
            (typeof textToCopy === 'function' ? false : !textToCopy)}
          aria-label={title}
          size={size}
          {...props}
        >
          {copied ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
              <CheckIcon sx={{ fontSize }} />
              {captionBelow && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    transform: 'translateY(50%) translateX(-24%)',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Copied
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <ContentCopyIcon sx={{ fontSize }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
